// Shared Nationals "About Wrap It Ceremony" card. Rendered in the homepage live
// banner and the Nationals stream Wrap It tab so the copy stays in one place.
// Nationals-specific (PACE scholarships, Collective announcement, cash prizes,
// fixed Thu June 25 / Showroom 1 details) — regional Wrap It keeps its own card.

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion.tsx'

const NATIONALS_WRAP_IT_FEATURES: Array<{
	text: string
	icon: 'cap' | 'star' | 'play'
}> = [
	{ text: 'Over $100K in DanceMakers Scholarships', icon: 'cap' },
	{ text: 'Over $200K in PACE University Scholarship', icon: 'cap' },
	{ text: 'Collective 2026-27 Announcement', icon: 'star' },
	{
		text: 'Special Performances from Audition Finalists and Solo Winners for 2025-26',
		icon: 'play',
	},
	{ text: 'All Duo/Trio and Group Overall Awards', icon: 'star' },
	{ text: 'Studio Awards', icon: 'star' },
	{ text: 'Performance of the Year for Duo/Trio and Group', icon: 'star' },
	{ text: 'With over $25k in cash prizes!', icon: 'star' },
]

function WrapItFeatureIcon({ type }: { type: 'cap' | 'star' | 'play' }) {
	const cls = 'mt-0.5 h-4 w-4 shrink-0 text-accent'
	if (type === 'cap') {
		return (
			<svg
				className={cls}
				fill="none"
				viewBox="0 0 24 24"
				stroke="currentColor"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth={2}
					d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
				/>
			</svg>
		)
	}
	if (type === 'play') {
		return (
			<svg
				className={cls}
				fill="none"
				viewBox="0 0 24 24"
				stroke="currentColor"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth={2}
					d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
				/>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth={2}
					d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
				/>
			</svg>
		)
	}
	return (
		<svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
			/>
		</svg>
	)
}

export function NationalsWrapItAbout({
	className,
	collapsible = false,
	defaultOpen = true,
}: {
	className?: string
	/** Render as a collapsible accordion (stream tab) vs. a static card (homepage). */
	collapsible?: boolean
	/** Initial open state when collapsible. Expanded before the ceremony, collapsed on/after the day. */
	defaultOpen?: boolean
}) {
	const heading = (
		<h3
			className="font-bold uppercase tracking-wide"
			style={{ fontFamily: 'var(--font-display)' }}
		>
			About Wrap It Ceremony
		</h3>
	)

	const body = (
		<>
			<p className="mb-4 text-xs text-muted-foreground">
				Join us at our Wrap It Ceremony for the finale of DanceMakers Nationals
				2025-26! It will be hosted on Thursday, June 25th, in Showroom 1 from
				12–2 PM with doors opening at 11:30 AM and will stream live right here.
				The Wrap It Ceremony features:
			</p>
			{/* Multi-column (not grid) so items fill top-to-bottom down the first
			    column before the second — i.e. column-first order. */}
			<ul className="columns-1 gap-x-4 text-xs text-muted-foreground sm:columns-2">
				{NATIONALS_WRAP_IT_FEATURES.map((feature, i) => (
					<li
						key={i}
						className="mb-2 flex break-inside-avoid items-start gap-2"
					>
						<WrapItFeatureIcon type={feature.icon} />
						{feature.text}
					</li>
				))}
			</ul>
			<p className="mt-4 text-xs text-muted-foreground">
				Everyone is welcome! No wristbands required. The ceremony will be
				available for playback after the event.
			</p>
		</>
	)

	if (collapsible) {
		return (
			<div
				className={`border border-muted-foreground/30 px-6${className ? ` ${className}` : ''}`}
			>
				<Accordion
					type="single"
					collapsible
					defaultValue={defaultOpen ? 'wrapit-about' : undefined}
				>
					<AccordionItem value="wrapit-about" className="border-b-0">
						<AccordionTrigger className="hover:no-underline">
							{heading}
						</AccordionTrigger>
						<AccordionContent className="pb-6">{body}</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>
		)
	}

	return (
		<div
			className={`border border-muted-foreground/30 p-6${className ? ` ${className}` : ''}`}
		>
			<div className="mb-3">{heading}</div>
			{body}
		</div>
	)
}
